/**
 * Scroll Keyframe Hero — GSAP ScrollTrigger canvas scrub
 *
 * Architecture:
 * - CSS position:sticky on viewport (no GSAP pin — eliminates gap issues)
 * - GSAP ScrollTrigger drives progress 0→1 with scrub
 * - Video-to-canvas scrub: loads MP4, seeks by currentTime, draws to canvas
 * - Frame-sequence-to-canvas: loads image sequence, maps scroll to frame index
 * - Waits for GSAP CDN to load before initializing
 */

(function () {
  'use strict';

  var instances = {};
  var MAX_GSAP_WAIT = 10000;
  var GSAP_POLL_INTERVAL = 100;

  /* ----------------------------------------
     Wait for GSAP globals
     ---------------------------------------- */
  function waitForGsap(callback) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      callback();
      return;
    }
    var elapsed = 0;
    var poll = setInterval(function () {
      elapsed += GSAP_POLL_INTERVAL;
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        clearInterval(poll);
        callback();
      } else if (elapsed >= MAX_GSAP_WAIT) {
        clearInterval(poll);
        console.warn('[SKH] GSAP/ScrollTrigger not available after ' + MAX_GSAP_WAIT + 'ms');
      }
    }, GSAP_POLL_INTERVAL);
  }

  /* ----------------------------------------
     Constructor
     ---------------------------------------- */
  function ScrollKeyframeHero(sectionEl) {
    if (!sectionEl) return;

    var self = this;
    self.el = sectionEl;
    self.id = sectionEl.dataset.sectionId;
    self.canvas = sectionEl.querySelector('.skh__canvas');
    self.ctx = self.canvas ? self.canvas.getContext('2d') : null;
    self.video = sectionEl.querySelector('.skh__video');
    self.poster = sectionEl.querySelector('.skh__poster');
    self.viewport = sectionEl.querySelector('.skh__viewport');
    self.progressBar = sectionEl.querySelector('.skh__progress-bar');
    self.callouts = Array.from(sectionEl.querySelectorAll('.skh__callout'));
    self.scrollTrigger = null;
    self.videoReady = false;
    self.canvasReady = false;
    self.destroyed = false;
    self._pendingSeek = false;
    self._lastSeekTime = 0;

    self.config = {
      scrubDistance: parseFloat(sectionEl.dataset.scrubDistance) || 2,
      enablePin: sectionEl.dataset.enablePin !== 'false',
      enableMobileScrub: sectionEl.dataset.enableMobileScrub !== 'false',
      useVideoFallback: sectionEl.dataset.useVideoFallback !== 'false',
      enableReducedMotion: sectionEl.dataset.enableReducedMotion !== 'false',
      objectFit: 'cover'
    };

    self.init();
  }

  ScrollKeyframeHero.prototype.init = function () {
    var self = this;

    if (self.prefersReducedMotion() && self.config.enableReducedMotion) {
      self.showStaticFallback();
      return;
    }

    var mobile = self.isMobile();
    self._isMobile = mobile;
    var mobileUrl = self.el.dataset.mobileVideoUrl;
    var desktopUrl = self.el.dataset.videoUrl;

    // On mobile: show static fallback only if scrub is disabled AND no mobile video
    if (mobile && !self.config.enableMobileScrub && !mobileUrl) {
      self.showStaticFallback();
      return;
    }

    if (!self.ctx || !self.video) return;

    // Desktop video src is already in HTML for instant loading.
    // On mobile, swap to the mobile video if available.
    if (mobile && mobileUrl) {
      self.video.src = mobileUrl;
      self.video.load();
    }

    self.sizeCanvas();
    self.bindResize();

    if (self.config.useVideoFallback) {
      self.initVideoScrub();
    } else {
      self.initFrameSequence();
    }
  };

  /* ----------------------------------------
     Video Scrub Mode
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.initVideoScrub = function () {
    var self = this;
    var video = self.video;

    self._onSeeked = function () {
      self._pendingSeek = false;
      self.drawVideoFrame();
    };
    video.addEventListener('seeked', self._onSeeked);

    function onVideoReady() {
      if (self.videoReady || self.destroyed) return;
      self.videoReady = true;
      if (self._loadTimeout) clearTimeout(self._loadTimeout);
      if (self._finalTimeout) clearTimeout(self._finalTimeout);
      video.removeEventListener('loadeddata', onVideoReady);
      video.removeEventListener('canplaythrough', onVideoReady);
      video.removeEventListener('canplay', onVideoReady);

      video.pause();
      video.currentTime = 0;

      video.addEventListener('seeked', function drawFirst() {
        video.removeEventListener('seeked', drawFirst);
        self.drawVideoFrame();
        self.revealCanvas();

        waitForGsap(function () {
          if (!self.destroyed) {
            self.createScrollTrigger();
          }
        });
      }, { once: true });

      if (video.currentTime === 0) {
        video.currentTime = 0.001;
      }
    }

    video.addEventListener('loadeddata', onVideoReady);
    video.addEventListener('canplaythrough', onVideoReady);
    video.addEventListener('canplay', onVideoReady);

    if (video.readyState >= 2) {
      onVideoReady();
      return;
    }

    // If native loading stalls, fetch as blob after 3s
    self._loadTimeout = setTimeout(function () {
      if (self.videoReady || self.destroyed) return;
      var src = video.src || video.getAttribute('src');
      if (!src) return;

      if (src.indexOf('//') === 0) src = 'https:' + src;

      fetch(src)
        .then(function (r) { return r.blob(); })
        .then(function (blob) {
          if (self.videoReady || self.destroyed) return;
          var url = URL.createObjectURL(blob);

          // Re-attach listeners before setting src — load() resets event queue
          video.addEventListener('loadeddata', onVideoReady);
          video.addEventListener('canplay', onVideoReady);

          video.src = url;
          video.load();

          // Force decode: play then immediately pause
          var playPromise = video.play();
          if (playPromise) {
            playPromise.then(function () { video.pause(); }).catch(function () {});
          }
        })
        .catch(function () {});
    }, 3000);

    // Final fallback: if video still hasn't loaded after 8s, show poster
    // and create ScrollTrigger anyway so the section scrolls correctly
    self._finalTimeout = setTimeout(function () {
      if (self.videoReady || self.destroyed) return;
      if (self.poster) self.poster.style.opacity = '1';
      waitForGsap(function () {
        if (!self.destroyed && !self.scrollTrigger) {
          self.createScrollTrigger();
        }
      });
    }, 8000);
  };

  ScrollKeyframeHero.prototype.drawVideoFrame = function () {
    var self = this;
    if (!self.ctx || !self.video || self.video.readyState < 2) return;

    var canvas = self.canvas;
    var video = self.video;
    var ctx = self.ctx;
    var vw = video.videoWidth;
    var vh = video.videoHeight;
    var cw = canvas.width;
    var ch = canvas.height;

    if (!vw || !vh) return;

    ctx.clearRect(0, 0, cw, ch);

    var scale, sw, sh, dx, dy;
    if (self.config.objectFit === 'cover') {
      scale = Math.max(cw / vw, ch / vh);
    } else {
      scale = Math.min(cw / vw, ch / vh);
    }
    sw = vw * scale;
    sh = vh * scale;
    dx = (cw - sw) / 2;
    dy = (ch - sh) / 2;

    ctx.drawImage(video, dx, dy, sw, sh);
  };

  /* ----------------------------------------
     Frame Sequence Mode (Production)
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.initFrameSequence = function () {
    var self = this;
    var manifest = window.__SKH_FRAMES;

    if (!manifest) {
      self.config.useVideoFallback = true;
      self.initVideoScrub();
      return;
    }

    var mobile = self.isMobile();
    var urls = (mobile && manifest.mobile) ? manifest.mobile : manifest.desktop;
    if (!urls || !urls.length) return;

    self.frames = [];
    self.frameCount = urls.length;
    self.framesLoaded = 0;

    var firstImg = new Image();
    firstImg.crossOrigin = 'anonymous';
    firstImg.onload = function () {
      self.frames[0] = firstImg;
      self.framesLoaded++;
      self.drawFrame(0);
      self.revealCanvas();

      waitForGsap(function () {
        if (!self.destroyed) {
          self.createScrollTrigger();
        }
      });

      loadRemaining();
    };
    firstImg.src = urls[0];

    function loadRemaining() {
      for (var i = 1; i < urls.length; i++) {
        (function (idx) {
          var img = new Image();
          img.crossOrigin = '';
          img.onload = function () {
            self.frames[idx] = img;
            self.framesLoaded++;
          };
          img.src = urls[idx];
        })(i);
      }
    }
  };

  ScrollKeyframeHero.prototype.drawFrame = function (index) {
    var self = this;
    if (!self.frames || !self.frames[index] || !self.ctx) return;

    var img = self.frames[index];
    var canvas = self.canvas;
    var ctx = self.ctx;
    var cw = canvas.width;
    var ch = canvas.height;
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;

    ctx.clearRect(0, 0, cw, ch);

    var scale, sw, sh, dx, dy;
    if (self.config.objectFit === 'cover') {
      scale = Math.max(cw / iw, ch / ih);
    } else {
      scale = Math.min(cw / iw, ch / ih);
    }
    sw = iw * scale;
    sh = ih * scale;
    dx = (cw - sw) / 2;
    dy = (ch - sh) / 2;

    ctx.drawImage(img, dx, dy, sw, sh);
  };

  /* ----------------------------------------
     GSAP ScrollTrigger with pin
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.createScrollTrigger = function () {
    var self = this;

    gsap.registerPlugin(ScrollTrigger);

    if (self.scrollTrigger) {
      self.scrollTrigger.kill();
      self.scrollTrigger = null;
    }

    self.scrollTrigger = ScrollTrigger.create({
      trigger: self.el,
      start: 'top top',
      end: 'bottom bottom',
      pin: self.viewport,
      pinSpacing: false,
      scrub: self.config.scrubDistance,
      invalidateOnRefresh: true,
      onUpdate: function (st) {
        self.applyProgress(st.progress);
      },
      onLeave: function () {
        self.applyProgress(1);
      },
      onEnter: function () {
        self.viewport.style.visibility = 'visible';
      },
      onEnterBack: function () {
        self.viewport.style.visibility = 'visible';
      },
      onLeaveBack: function () {
        self.applyProgress(0);
      }
    });
  };

  ScrollKeyframeHero.prototype.applyProgress = function (progress) {
    var self = this;
    var p = Math.max(0, Math.min(1, progress));

    if (self.config.useVideoFallback && self.video && self.videoReady) {
      var duration = self.video.duration;
      if (duration && isFinite(duration)) {
        var targetTime = p * duration;
        var delta = Math.abs(self.video.currentTime - targetTime);
        var now = performance.now();

        // Auto-clear pendingSeek after 100ms to prevent stuck state on any platform
        if (self._pendingSeek && now - (self._lastSeekTime || 0) > 100) {
          self._pendingSeek = false;
        }

        if (delta > 0.05 && !self._pendingSeek) {
          self._pendingSeek = true;
          self._lastSeekTime = now;
          self.video.currentTime = targetTime;
        }
      }
    } else if (self.frames && self.frameCount) {
      var frameIdx = Math.min(Math.floor(p * self.frameCount), self.frameCount - 1);
      self.drawFrame(frameIdx);
    }

    if (self.progressBar) {
      self.progressBar.style.width = (p * 100) + '%';
    }

    self.updateCallouts(p * 100);
  };

  /* ----------------------------------------
     Feature Callout Visibility
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.updateCallouts = function (progressPct) {
    var deadband = 1.5;
    this.callouts.forEach(function (el) {
      var start = parseFloat(el.dataset.progressStart) || 0;
      var end = parseFloat(el.dataset.progressEnd) || 100;
      var isCurrentlyVisible = el.classList.contains('skh__callout--visible');

      if (isCurrentlyVisible) {
        if (progressPct < (start - deadband) || progressPct > (end + deadband)) {
          el.classList.remove('skh__callout--visible');
        }
      } else {
        if (progressPct >= (start + deadband) && progressPct <= (end - deadband)) {
          el.classList.add('skh__callout--visible');
        }
      }
    });
  };

  /* ----------------------------------------
     Canvas Management
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.sizeCanvas = function () {
    var self = this;
    if (!self.canvas || !self.viewport) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = self.viewport.clientWidth;
    var h = self.viewport.clientHeight;

    self.canvas.width = w * dpr;
    self.canvas.height = h * dpr;
    // Let CSS inset: 0 handle element sizing — no explicit style dimensions
    self.canvas.style.width = '';
    self.canvas.style.height = '';
  };

  ScrollKeyframeHero.prototype.revealCanvas = function () {
    this.el.classList.add('skh--canvas-ready');
    this.canvasReady = true;
  };

  /* ----------------------------------------
     Resize
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.bindResize = function () {
    var self = this;
    var resizeTimeout;

    self._resizeHandler = function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        if (self.destroyed) return;
        self.sizeCanvas();

        if (self.config.useVideoFallback) {
          self.drawVideoFrame();
        } else if (self.frames) {
          var st = self.scrollTrigger;
          var prog = st ? st.progress : 0;
          var frameIdx = Math.min(
            Math.floor(prog * (self.frameCount || 1)),
            (self.frameCount || 1) - 1
          );
          self.drawFrame(frameIdx);
        }

        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      }, 150);
    };

    window.addEventListener('resize', self._resizeHandler);
  };

  /* ----------------------------------------
     Helpers
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.isMobile = function () {
    // Use both viewport width AND touch capability for reliable detection
    var narrowViewport = window.innerWidth <= 768;
    var hasTouch = ('ontouchstart' in window) ||
                   (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
    var mobileUA = /Android|iPhone|iPod|iPad/i.test(navigator.userAgent);

    // Mobile = narrow viewport, OR touch device with mobile UA (catches tablets/phones > 768px)
    return narrowViewport || (hasTouch && mobileUA && window.innerWidth <= 1024);
  };

  ScrollKeyframeHero.prototype.isMobileScrubEnabled = function () {
    return this.config.enableMobileScrub;
  };

  ScrollKeyframeHero.prototype.prefersReducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  ScrollKeyframeHero.prototype.showStaticFallback = function () {
    if (this.poster) {
      this.poster.style.opacity = '1';
    }
    this.el.style.height = 'auto';
  };

  /* ----------------------------------------
     Destroy
     ---------------------------------------- */
  ScrollKeyframeHero.prototype.destroy = function () {
    var self = this;
    self.destroyed = true;

    if (self.scrollTrigger) {
      self.scrollTrigger.kill();
      self.scrollTrigger = null;
    }

    if (self._resizeHandler) {
      window.removeEventListener('resize', self._resizeHandler);
    }

    if (self._loadTimeout) {
      clearTimeout(self._loadTimeout);
    }

    if (self.video && self._onSeeked) {
      self.video.removeEventListener('seeked', self._onSeeked);
    }

    self.el.classList.remove('skh--canvas-ready');
    delete instances[self.id];
  };

  /* ----------------------------------------
     Global Init
     ---------------------------------------- */
  function initAll() {
    var sections = document.querySelectorAll('[data-section-type="scroll-keyframe-hero"]');
    sections.forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && !instances[id]) {
        instances[id] = new ScrollKeyframeHero(el);
      }
    });
  }

  function destroyInstance(id) {
    if (instances[id]) {
      instances[id].destroy();
    }
  }

  /* ----------------------------------------
     Bootstrap
     ---------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ----------------------------------------
     Shopify Theme Editor Lifecycle
     ---------------------------------------- */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-section-type="scroll-keyframe-hero"]');
    if (el) {
      var id = el.dataset.sectionId;
      destroyInstance(id);
      setTimeout(function () {
        instances[id] = new ScrollKeyframeHero(el);
      }, 100);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-section-type="scroll-keyframe-hero"]');
    if (el) {
      destroyInstance(el.dataset.sectionId);
    }
  });

  document.addEventListener('shopify:section:select', function (e) {
    var el = e.target.querySelector('[data-section-type="scroll-keyframe-hero"]');
    if (el && instances[el.dataset.sectionId]) {
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    }
  });

})();
